/// <reference path="../../app.d.ts"/>

import { AppUtils } from '../../../../../shared/utils/index';
import { BasicApplicationService } from '../../../../../shared/services/BasicApplicationService';
import { User } from '../../../../../shared/models/api/User/User';
import { BasicUserService } from '../../../../../shared/services/BasicUserService';
import { AppPersistenceService } from '../services/AppPersistenceService';
import { AppApiService } from '../services/AppApiService';
import { AppRouterService } from '../services/AppRouterService';
import { ApplicationPages } from '../Constants';
import { defaults } from '../defaults';

import { OverviewPageController } from './steps/OverviewPageController';
import { InputPageController } from './steps/InputPageController';
import { AppFormModel } from '../models/AppFormModel';
import { GarageTypeApi } from '../../../../../shared/models/generated/FahrzeugdatenGarageTypeApi';

export class App implements AppController {
	private stepController?: StepController;
	private readonly appPersistanceService: AppPersistenceService;
	private readonly userService: BasicUserService;
	private readonly $ctx: JQuery;
	private user: User;
	private readonly applicationRouter: AppRouterService;

	private readonly loader: BasicLoader<MyVehicleAppDefinition>;
	private readonly applicationService: BasicApplicationService<MyVehicleAppDefinition>;
	private readonly appApiService: AppApiService;
	public options: BasicAppOptions<MyVehicleAppDefinition>;


	constructor(element: HTMLElement | JQuery, options: BasicAppOptions<MyVehicleAppDefinition>, loader: BasicLoader<MyVehicleAppDefinition>) {
		this.loader = loader;
		this.options = $.extend(true, {}, defaults, options);
		this.$ctx = this.options.$ctx;

		this.userService = new BasicUserService();
		this.applicationRouter = new AppRouterService(this.options);
		this.appPersistanceService = new AppPersistenceService(this.options);
		this.applicationService = new BasicApplicationService(this.options);
		this.appApiService = new AppApiService(this, this.options);
	}

	public start(): void {
		this.callDependencyLoader();
	}

	public callDependencyLoader(): void {
		this.loader._loadDependencies(this.initApplication.bind(this));
	}

	public initApplication(): void {

		AppUtils.View.startLoader();
		this.appPersistanceService.prepareApplicationSession();
		this.userService.getUser((user: User) => {
			this.user = user;
			if (this.checkPreconditions()) {
				this.startApp();
			} else if (this.user.Valid) {
				this.applicationRouter.error();
			} else {
				this.applicationRouter.login();
			}
		});
	}

	private startApp(): void {
		const urlParams = AppUtils.Url.getParamsAsSingle(window.location.search);
		const vehicleID = urlParams.myVehicleId || urlParams.id;
		const kfzId = urlParams.kfzid;

		if (kfzId && !vehicleID) {
			const formModel = new AppFormModel();
			this.loadVehicleDetailsAndStart(kfzId, formModel);
		} else if (kfzId && vehicleID) {
			this.appApiService.getVehicles(vehicleID, (formModel) => {
				this.loadVehicleDetailsAndStart(kfzId, formModel);
			}, () => this.startInputPage(kfzId));
		} else if (vehicleID) {
			this.appApiService.getVehicles(vehicleID, (formModel) => {
				this.loadVehicleDetailsAndStart(kfzId || formModel.Fahrzeug.KfzId, formModel, false);
			}, () => this.startOverviewPage());
		} else {
			this.startInputPage();
		}
	}

	private loadVehicleDetailsAndStart(kfzId: string, formModel: AppFormModel, fromCatalogue = true): void {
		if (formModel.Fahrzeug && GarageTypeApi.Anhaenger === formModel.Fahrzeug.Fahrzeugart) {
			formModel.Fahrzeug.Fahrzeugart = GarageTypeApi.Sonstige;
		}
		const startFunc = fromCatalogue ? (): void => this.startInputPage(kfzId) : (): void => this.startOverviewPage(formModel);
		if (kfzId) {
			this.appApiService.getFahrzeugDetails(kfzId, formModel, () => {
				if (formModel.Fahrzeug && formModel.Fahrzeug.ID) {
					formModel.Fahrzeug.Karosserievariante = "";
				}
				this.appPersistanceService.setFormModelToStore(formModel);
				startFunc();
			}, startFunc);
		} else {
			startFunc();
		}
	}

	private startOverviewPage(formModel?: AppFormModel): void {
		const overviewOptions = this.applicationService.preparePageOptions(ApplicationPages.OverviewPage);
		this.stepController = new OverviewPageController(this, overviewOptions, this.loader, this.user, formModel);
		this.stepController.start();
	}

	private startInputPage(kfzId?: string): void {
		const inputOptions = this.applicationService.preparePageOptions(ApplicationPages.InputPage);
		const stepController = new InputPageController(this, inputOptions, this.loader, this.user, !!kfzId, kfzId);
		stepController.start();
	}

	/**
	 * User must be available and a member (if it is an plusupgrade the user has to be non-plus).
	 */
	private checkPreconditions(): boolean {
		return this.user.Customer || this.user.ValidMember;
	}

	afterRenderApp(): void {
		this.loader.initAdditionalModules(this.$ctx);
		S.Utils.Lightbox.init();
		AppUtils.View.stopLoader();
		this.loader.complete();
	}
}
